import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme
} from '@mui/material';
import { AccessTime, Warning, WbSunny, LightMode, Brightness3 } from '@mui/icons-material';
import { useAvailableTimeSlots } from '../hooks/useAvailableTimeSlots';
import { Service } from '../types';

interface TimeSlotPickerProps {
  storeId: string;
  selectedStaffId: string;
  selectedService: Service | null;
  selectedDate: string;
  selectedTime: string;
  onTimeSelect: (time: string) => void;
  disabled?: boolean;
}

interface TimeSlotGroup {
  label: string;
  icon: React.ReactNode;
  color: string;
  slots: Array<{ time: string; disabled: boolean; reason?: string }>;
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  storeId,
  selectedStaffId,
  selectedService,
  selectedDate,
  selectedTime,
  onTimeSelect,
  disabled = false
}) => {
  const theme = useTheme();
  
  const { timeSlots, loading, error } = useAvailableTimeSlots({
    storeId,
    selectedStaffId,
    selectedService,
    selectedDate
  });

  // Helper function to convert time string to minutes for sorting
  const timeToMinutes = (timeString: string): number => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Group time slots by time periods with proper sorting
  const groupedTimeSlots = useMemo((): TimeSlotGroup[] => {
    if (timeSlots.length === 0) return [];

    // Sort all slots chronologically first
    const sortedSlots = [...timeSlots].sort((a, b) => 
      timeToMinutes(a.time) - timeToMinutes(b.time)
    );

    const groups: TimeSlotGroup[] = [
      {
        label: 'Morning',
        icon: <WbSunny />,
        color: theme.palette.warning.main,
        slots: []
      },
      {
        label: 'Afternoon', 
        icon: <LightMode />,
        color: theme.palette.primary.main,
        slots: []
      },
      {
        label: 'Evening',
        icon: <Brightness3 />,
        color: theme.palette.secondary.main,
        slots: []
      }
    ];

    // Categorize slots into time periods
    sortedSlots.forEach(slot => {
      const minutes = timeToMinutes(slot.time);
      
      if (minutes < 12 * 60) { // Before 12:00
        groups[0].slots.push(slot);
      } else if (minutes < 17 * 60) { // 12:00 - 16:59
        groups[1].slots.push(slot);
      } else { // 17:00 and later
        groups[2].slots.push(slot);
      }
    });

    // Return only groups that have slots
    return groups.filter(group => group.slots.length > 0);
  }, [timeSlots, theme]);

  const availableCount = timeSlots.filter(slot => !slot.disabled).length;

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 4,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading available time slots...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        Failed to load available time slots. Please try again.
      </Alert>
    );
  }

  if (!selectedStaffId || !selectedService || !selectedDate) {
    return (
      <Paper 
        sx={{ 
          p: 4, 
          textAlign: 'center',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.default'
        }}
      >
        <AccessTime sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Select Staff, Service, and Date
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose a staff member, service, and date to see available time slots.
        </Typography>
      </Paper>
    );
  }

  if (timeSlots.length === 0) {
    return (
      <Paper 
        sx={{ 
          p: 4, 
          textAlign: 'center',
          border: 1,
          borderColor: 'warning.main',
          borderRadius: 2,
          bgcolor: 'warning.light',
          color: 'warning.contrastText'
        }}
      >
        <Warning sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          No Available Time Slots
        </Typography>
        <Typography variant="body2">
          The selected staff member is not available on this date, or their schedule hasn't been set up yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTime sx={{ color: 'primary.main' }} />
          Available Time Slots
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {availableCount} available slots for {selectedService?.name} ({selectedService?.duration} minutes)
        </Typography>
      </Box>

      {/* Time slots grouped by period */}
      <Box
        sx={{
          maxHeight: 450,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: theme.palette.grey[100],
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: '4px',
          },
        }}
      >
        {groupedTimeSlots.map((group) => (
          <Paper 
            key={group.label}
            elevation={1}
            sx={{ 
              p: 3, 
              mb: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              '&:last-child': { mb: 0 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box 
                sx={{ 
                  p: 1, 
                  borderRadius: '50%', 
                  bgcolor: `${group.color}20`,
                  color: group.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {group.icon}
              </Box>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 700,
                  color: group.color,
                  fontSize: '1.1rem'
                }}
              >
                {group.label}
              </Typography>
              <Chip 
                label={`${group.slots.filter(slot => !slot.disabled).length} available`}
                size="small"
                sx={{ 
                  bgcolor: `${group.color}15`,
                  color: group.color,
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              />
            </Box>
            
            <Grid container spacing={1.5}>
              {group.slots.map((slot) => (
                <Grid item key={slot.time}>
                  <Tooltip 
                    title={slot.disabled ? slot.reason || 'Not available' : 'Click to select this time'}
                    placement="top"
                    arrow
                  >
                    <span>
                      <Chip
                        label={slot.time}
                        onClick={() => !slot.disabled && !disabled && onTimeSelect(slot.time)}
                        disabled={slot.disabled || disabled}
                        variant={selectedTime === slot.time ? 'filled' : 'outlined'}
                        sx={{
                          cursor: slot.disabled || disabled ? 'not-allowed' : 'pointer',
                          minWidth: 80,
                          height: 40,
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          border: selectedTime === slot.time ? 'none' : `2px solid ${theme.palette.divider}`,
                          bgcolor: selectedTime === slot.time ? group.color : 
                                 slot.disabled ? 'action.disabledBackground' : 'background.paper',
                          color: selectedTime === slot.time ? 'white' :
                                 slot.disabled ? 'text.disabled' : 'text.primary',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            bgcolor: slot.disabled || disabled ? 'action.disabledBackground' :
                                    selectedTime === slot.time ? group.color : 
                                    `${group.color}15`,
                            transform: slot.disabled || disabled ? 'none' : 'translateY(-2px)',
                            boxShadow: slot.disabled || disabled ? 'none' : theme.shadows[4],
                            borderColor: slot.disabled || disabled ? theme.palette.divider : group.color
                          },
                          '&.Mui-disabled': {
                            opacity: 0.4,
                            cursor: 'not-allowed'
                          },
                          boxShadow: selectedTime === slot.time ? theme.shadows[3] : 'none'
                        }}
                      />
                    </span>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          </Paper>
        ))}
      </Box>

      {availableCount === 0 && (
        <Alert 
          severity="warning" 
          sx={{ 
            mt: 2, 
            borderRadius: 2,
            border: 1,
            borderColor: 'warning.main'
          }}
        >
          All time slots are unavailable for this date. Please select a different date or staff member.
        </Alert>
      )}
    </Box>
  );
};

export default TimeSlotPicker;
